import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { client, ReferenceInstance, ScopedReferenceMarket } from '../app/services/api/orderManagementClient';
import { UserContext } from '../components/shared/useUser';
import useLogError from '../hooks/useLogError';
import { loadAccessibleInstances, loadAccessibleMarkets } from './referenceLookup';

interface InstanceMarketContextType {
  instances: ReferenceInstance[];
  instancesLoading: boolean;
  refreshInstances: () => Promise<void>;

  /** Returns the cached markets for an instance (empty array if not loaded yet). */
  getMarkets: (instanceId: string) => ScopedReferenceMarket[];
  /** True while markets for the given instance are being fetched. */
  isMarketsLoading: (instanceId: string) => boolean;
  /** Triggers a fetch of markets for the instance if not already cached/loading. */
  ensureMarketsLoaded: (instanceId: string) => void;
  /** Forces a re-fetch of markets for the instance, refreshing the cache. */
  refreshMarkets: (instanceId: string) => Promise<ScopedReferenceMarket[]>;

  /** Optional UX defaults derived from a selected store within authorised scope. */
  defaultInstanceId: string;
  defaultMarketId: string;
  defaultFiltersLoading: boolean;

}

const InstanceMarketContext = createContext<InstanceMarketContextType | undefined>(undefined);

interface InstanceMarketProviderProps {
  children: ReactNode;
}

export const InstanceMarketProvider: React.FC<InstanceMarketProviderProps> = ({ children }) => {
  const { logError } = useLogError();
  const { user, selectedStore, userFranchisesCurrentTenant } = useContext(UserContext);

  const [instances, setInstances] = useState<ReferenceInstance[]>([]);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(false);
  const instancesLoadedRef = useRef<boolean>(false);
  const instancesLoadingPromiseRef = useRef<Promise<void> | null>(null);

  const [marketsByInstance, setMarketsByInstance] = useState<Record<string, ScopedReferenceMarket[]>>({});
  const [marketsLoadingByInstance, setMarketsLoadingByInstance] = useState<Record<string, boolean>>({});
  const marketsLoadingPromisesRef = useRef<Record<string, Promise<ScopedReferenceMarket[]> | undefined>>({});

  const [defaultInstanceId, setDefaultInstanceId] = useState('');
  const [defaultMarketId, setDefaultMarketId] = useState('');
  const [defaultFiltersLoading, setDefaultFiltersLoading] = useState(false);

  const accessScopeVersionRef = useRef(0);

  const fetchInstances = useCallback(async (): Promise<void> => {
    const accessScopeVersion = accessScopeVersionRef.current;
    setInstancesLoading(true);
    try {
      const accessibleInstances = await loadAccessibleInstances(client);
      if (accessScopeVersion !== accessScopeVersionRef.current) return;
      setInstances(accessibleInstances);
      instancesLoadedRef.current = true;
    } catch (error) {
      logError('Failed to load instances');
    } finally {
      setInstancesLoading(false);
    }
  }, [logError]);

  const refreshInstances = useCallback(async (): Promise<void> => {
    instancesLoadingPromiseRef.current = fetchInstances();
    await instancesLoadingPromiseRef.current;
    instancesLoadingPromiseRef.current = null;
  }, [fetchInstances]);

  const invalidateScopedLookups = useCallback(() => {
    accessScopeVersionRef.current += 1;
    instancesLoadedRef.current = false;
    instancesLoadingPromiseRef.current = null;
    marketsLoadingPromisesRef.current = {};
    setInstances([]);
    setMarketsByInstance({});
    setMarketsLoadingByInstance({});
  }, []);

  // Reference data belongs to the authenticated administrative session, not a
  // selected store. A user-context reload starts a new reference-data cache.
  const accessScopeKey = `${user?.id ?? ''}|${user?.roles?.join(',') ?? ''}|${userFranchisesCurrentTenant.map(franchise => franchise.franchiseId).sort().join(',')}`;
  useEffect(() => {
    invalidateScopedLookups();
  }, [accessScopeKey, invalidateScopedLookups]);

  // Load instances once for each authorization context.
  useEffect(() => {
    if (!instancesLoadedRef.current && !instancesLoadingPromiseRef.current) {
      instancesLoadingPromiseRef.current = fetchInstances();
    }
  }, [accessScopeKey, fetchInstances]);

  const fetchMarkets = useCallback(async (instanceId: string): Promise<ScopedReferenceMarket[]> => {
    const accessScopeVersion = accessScopeVersionRef.current;
    setMarketsLoadingByInstance(prev => ({ ...prev, [instanceId]: true }));
    try {
      const items = await loadAccessibleMarkets(client, instanceId);
      if (accessScopeVersion !== accessScopeVersionRef.current) return [];
      setMarketsByInstance(prev => ({ ...prev, [instanceId]: items }));
      return items;
    } catch (error) {
      logError('Failed to load markets');
      return [];
    } finally {
      setMarketsLoadingByInstance(prev => ({ ...prev, [instanceId]: false }));
      delete marketsLoadingPromisesRef.current[instanceId];
    }
  }, [logError]);

  const refreshMarkets = useCallback((instanceId: string): Promise<ScopedReferenceMarket[]> => {
    if (!instanceId) return Promise.resolve([]);
    const promise = fetchMarkets(instanceId);
    marketsLoadingPromisesRef.current[instanceId] = promise;
    return promise;
  }, [fetchMarkets]);

  const ensureMarketsLoaded = useCallback((instanceId: string): void => {
    if (!instanceId) return;
    if (marketsByInstance[instanceId] || marketsLoadingPromisesRef.current[instanceId]) return;
    marketsLoadingPromisesRef.current[instanceId] = fetchMarkets(instanceId);
  }, [fetchMarkets, marketsByInstance]);

  const getMarkets = useCallback((instanceId: string): ScopedReferenceMarket[] => {
    if (!instanceId) return [];
    return marketsByInstance[instanceId] || [];
  }, [marketsByInstance]);

  const isMarketsLoading = useCallback((instanceId: string): boolean => {
    if (!instanceId) return false;
    return !!marketsLoadingByInstance[instanceId];
  }, [marketsLoadingByInstance]);

  // The selected store is only an initial UI preference. It is matched against
  // the already-authorised reference response and never affects cache scope.
  useEffect(() => {
    let cancelled = false;
    const setStoreDefaults = async () => {
      setDefaultInstanceId('');
      setDefaultMarketId('');
      if (!selectedStore?.franchiseUUID) return;

      setDefaultFiltersLoading(true);
      try {
        const markets = await loadAccessibleMarkets(client);
        if (cancelled) return;
        const market = markets.find(candidate => candidate.uuid === selectedStore.franchiseUUID);
        if (market) {
          setDefaultInstanceId(market.instanceId.toString());
          setDefaultMarketId(market.id.toString());
        }
      } catch (error) {
        logError('Failed to load selected-store defaults');
      } finally {
        if (!cancelled) setDefaultFiltersLoading(false);
      }
    };

    setStoreDefaults();
    return () => { cancelled = true; };
  }, [selectedStore?.franchiseUUID, accessScopeKey, logError]);

  const value: InstanceMarketContextType = useMemo(() => ({
    instances,
    instancesLoading,
    refreshInstances,
    getMarkets,
    isMarketsLoading,
    ensureMarketsLoaded,
    refreshMarkets,
    defaultInstanceId,
    defaultMarketId,
    defaultFiltersLoading,
  }), [
    instances,
    instancesLoading,
    refreshInstances,
    getMarkets,
    isMarketsLoading,
    ensureMarketsLoaded,
    refreshMarkets,
    defaultInstanceId,
    defaultMarketId,
    defaultFiltersLoading,
  ]);

  return (
    <InstanceMarketContext.Provider value={value}>
      {children}
    </InstanceMarketContext.Provider>
  );
};

export const useInstanceMarket = (): InstanceMarketContextType => {
  const context = useContext(InstanceMarketContext);
  if (context === undefined) {
    throw new Error('useInstanceMarket must be used within an InstanceMarketProvider');
  }
  return context;
};
